import useLocalizedCyclingFitnessData from "@/components/FitnessProfile/CyclingProfile/useLocalizedCyclingFitnessData";
import { getCyclingLegendItems } from "@/components/FitnessProfile/Shared/legendItems";
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import { useTheme } from "@mui/material";
import {
  FitnessProfileAlgorithm,
  FitnessProfileType,
  FourDPQuizResultsType,
  TrainingProgressionUpdateType,
  WahooWorkoutTypeFamily,
} from "@WahooFitness/cloud-client-types";
import {
  postClose,
  useCloudContext,
  useConfigContext,
  useDrawerContext,
  useGuardedUpdater,
  useHeaderContext,
} from "@WahooFitness/wahoo-offline-mfe";
import { TrainingProgressions } from "@WahooFitness/cloud-client-ts";
import useFitnessProfiles from "@/hooks/useFitnessProfiles";
import useUserProfiles from "@/hooks/useUserProfiles";
import { t } from "@lingui/macro";
import FourDPPages from "../FourDPPages";
import { useLogEvent } from "@/services/Analytics/useLogEvent";
import { ClickTarget, SourcePage } from "@/services/Analytics/EventTypes";
import HowWeCalculateDrawer from "@/components/FitnessProfile/HowWeCalculateDrawer";
import { useNavigate } from "react-router";
import DiscardResultsDrawer from "../../Results/DiscardResultsDrawer";
import FitnessTestDrawer from "../../Results/FitnessTestDrawer";
import { TrendIcon } from "@WahooFitness/redesignr";

const useQuizResults = (
  setPage: Dispatch<SetStateAction<number>>,
  results?: FourDPQuizResultsType,
  closeOnBack?: boolean,
  initialTrainingProgression?: TrainingProgressionUpdateType
) => {
  const [resultsLoading, setResultsLoading] = useState(false);
  const { getCloudClient } = useCloudContext();
  const { wahooToken } = useConfigContext();
  const { update } = useGuardedUpdater();
  const { logEvent } = useLogEvent();
  const { setDrawer } = useDrawerContext();
  const analyticsContext = useMemo(
    () => ({
      name: "4dp_quiz",
      data: { source_page_name: SourcePage.fourdp_quiz_results },
    }),
    []
  );
  const navigate = useNavigate();
  useEffect(() => {
    setResultsLoading(true);
    const timeoutId = setTimeout(() => {
      setResultsLoading(false);
    }, 2000);
    return () => clearTimeout(timeoutId);
  }, []);

  const reloadHandler = useCallback(() => {
    navigate("/fitness-profile");
  }, [navigate]);

  const { navigateBack } = useHeaderContext();

  const { getRiderTypeById, getRiderTypeStrengthById, getRiderTypeWeaknessById } =
    useLocalizedCyclingFitnessData(32);

  const riderTypeData = useMemo(
    () => getRiderTypeById(results?.profile?.athlete?.type),
    [results, getRiderTypeById]
  );

  const riderTypeStrength = useMemo(
    () => getRiderTypeStrengthById(results?.profile?.athlete?.strength),
    [results, getRiderTypeStrengthById]
  );

  const riderTypeWeakness = useMemo(
    () => getRiderTypeWeaknessById(results?.profile?.athlete?.weakness),
    [results, getRiderTypeWeaknessById]
  );

  const riderStrength = useMemo(() => {
    if (riderTypeStrength) {
      return {
        name: riderTypeStrength?.name,
        description: riderTypeStrength?.description,
        icon: <TrendIcon size="small" direction="up" color="success" />,
      };
    }
  }, [riderTypeStrength]);

  const riderWeakness = useMemo(() => {
    if (riderTypeWeakness) {
      return {
        name: riderTypeWeakness?.name,
        description: riderTypeWeakness?.description,
        icon: <TrendIcon size="small" direction="down" color="error" />,
      };
    }
  }, [riderTypeWeakness]);

  const trainingProgressionsClient = useMemo(
    () => getCloudClient(TrainingProgressions),
    [getCloudClient]
  );

  const { cyclingFitnessProfile, createOrUpdateFitnessProfile } = useFitnessProfiles();
  const { userProfilesData, updateUserProfiles } = useUserProfiles();

  const navigateToZones = useCallback(() => {
    setPage(FourDPPages.zones);
  }, [setPage]);

  const saveFitnessProfile = useCallback(async () => {
    if (!results?.profile) return;
    logEvent(analyticsContext.name, {
      ...analyticsContext.data,
      click_target: ClickTarget.save,
    });
    const fitnessProfileUpdate: Omit<
      FitnessProfileType,
      "user_id" | "dur_1_src" | "dur_2_src" | "dur_3_src" | "dur_4_src"
    > = {
      workout_type_family_id: WahooWorkoutTypeFamily.Cycling,
      fitness_type_id: results.profile.athlete.type,
      fitness_weakness_id: results.profile.athlete.weakness,
      fitness_strength_id: results.profile.athlete.strength,
      dim_1: results.profile.fourdp.FTP,
      dim_2: results.profile.fourdp.MAP,
      dim_3: results.profile.fourdp.AC,
      dim_4: results.profile.fourdp.NM,
      dim_1_rank: results.profile.rank.FTP,
      dim_2_rank: results.profile.rank.MAP,
      dim_3_rank: results.profile.rank.AC,
      dim_4_rank: results.profile.rank.NM,
      fitness_profile_algorithm_id: FitnessProfileAlgorithm.FourDPQuiz,
      threshold_hr: cyclingFitnessProfile?.threshold_hr ?? null,
      preferred_cadence: cyclingFitnessProfile?.preferred_cadence ?? null,
      // below are for running - once we have the running quiz we can replace with actual data
      dur_1: null,
      dur_2: null,
      dur_3: null,
      dur_4: null,
    };
    if (initialTrainingProgression) {
      update(() => trainingProgressionsClient.post(wahooToken, initialTrainingProgression));
    }

    return update(
      () => createOrUpdateFitnessProfile(fitnessProfileUpdate, cyclingFitnessProfile?.id),
      {
        afterUpdate: async () => {
          if (!userProfilesData?.workout_type_family_preferences?.length) {
            await updateUserProfiles({
              workoutTypeFamilyPreferences: [
                WahooWorkoutTypeFamily.Cycling,
                WahooWorkoutTypeFamily.Running,
              ],
            });
          } else if (
            !userProfilesData.workout_type_family_preferences.includes(
              WahooWorkoutTypeFamily.Cycling
            )
          ) {
            await updateUserProfiles({
              workoutTypeFamilyPreferences: [
                ...userProfilesData.workout_type_family_preferences,
                WahooWorkoutTypeFamily.Cycling,
              ],
            });
          }
          navigateToZones();
        },
        errorMessage: t`There was a problem saving your profile. Check your network and try again.`,
      }
    );
  }, [
    trainingProgressionsClient,
    initialTrainingProgression,
    cyclingFitnessProfile,
    createOrUpdateFitnessProfile,
    logEvent,
    navigateToZones,
    results?.profile,
    update,
    wahooToken,
    analyticsContext,
    userProfilesData,
    updateUserProfiles,
  ]);

  const handleCloseDrawer = useCallback(() => {
    setDrawer({ open: false });
  }, [setDrawer]);

  const handleDiscardResults = useCallback(() => {
    logEvent(analyticsContext.name, {
      ...analyticsContext.data,
      click_target: ClickTarget.discard_results,
    });
    setDrawer({ open: false });
    if (closeOnBack) {
      postClose("fourdp");
    } else {
      navigateBack();
    }
  }, [closeOnBack, logEvent, navigateBack, setDrawer, analyticsContext]);

  const openInfoDrawer = useCallback(() => {
    logEvent(analyticsContext.name, {
      ...analyticsContext.data,
      click_target: ClickTarget.how_we_calculate,
    });
    setDrawer({
      open: true,
      title: t`How we calculate?`,
      hidePuller: true,
      children: (
        <HowWeCalculateDrawer
          infoText={t`This chart visualizes your Athlete Profile and compares how each metric ranks to one another for your specific demographic. It factors in your height, weight, age, sex and fitness level to determine your abilities. If a value falls in the modest range, it highlights areas for improvement. If the value falls in the exceptional range, it highlights your strengths as a rider.`}
        />
      ),
      elevation: 4,
    });
  }, [logEvent, setDrawer, analyticsContext]);

  const discardResults = useCallback(() => {
    setDrawer({
      open: true,
      hidePuller: true,
      children: (
        <DiscardResultsDrawer onCancel={handleCloseDrawer} onDiscard={handleDiscardResults} />
      ),
      elevation: 4,
      hideHeader: true,
    });
  }, [setDrawer, handleCloseDrawer, handleDiscardResults]);

  const openFitnessTestDrawer = useCallback(() => {
    setDrawer({
      open: true,
      title: t`Why is this an estimate?`,
      hidePuller: true,
      children: <FitnessTestDrawer />,
      elevation: 4,
    });
  }, [setDrawer]);

  const { palette } = useTheme();

  const dimensions = useMemo(
    () => ({
      ftp: {
        value: results?.profile?.fourdp?.FTP ?? 0,
        rank: results?.profile?.rank?.FTP ?? 0,
      },
      map: {
        value: results?.profile?.fourdp?.MAP ?? 0,
        rank: results?.profile?.rank?.MAP ?? 0,
      },
      ac: {
        value: results?.profile?.fourdp?.AC ?? 0,
        rank: results?.profile?.rank?.AC ?? 0,
      },
      nm: {
        value: results?.profile?.fourdp?.NM ?? 0,
        rank: results?.profile?.rank?.NM ?? 0,
      },
    }),
    [results?.profile?.fourdp, results?.profile?.rank]
  );

  const legendItems = useMemo(() => getCyclingLegendItems(palette), [palette]);

  return {
    resultsLoading,
    riderTypeData,
    riderWeakness,
    riderStrength,
    dimensions,
    legendItems,
    saveFitnessProfile,
    openInfoDrawer,
    openFitnessTestDrawer,
    discardResults,
    reloadHandler,
    analyticsContext,
  };
};

export default useQuizResults;
